import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PreviewIcon from '@mui/icons-material/Preview';
import PublicIcon from '@mui/icons-material/Public';
import { Menu, MenuItem, MenuProps } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { forwardRef, Fragment, memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseMiniMenuProps } from './MiniMenuSelector';
import { bloxValues } from 'themes/constants';
import { WireSearchResultItem } from 'utility/types/dataProvider';
import {
  checkSiteAdminPermission,
  checkSyndicationFullAccessPermission,
  checkSyndicationImportContentPermission,
  PermissionInfo,
} from 'utility/permissions';
import { useNxtImport } from 'components/ExportImport/NxtImport/NxtImportContext';
import { useWireSearch } from 'screens/syndication/views/search/syndication/WireSearchWithContext';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useTheme } from '@mui/material';
import { isAssetTypeEditorEnabled } from 'utility/functions';
import { useLocation } from 'react-router-dom';

export type MuiCloseEvent = Partial<Parameters<NonNullable<MenuProps['onClose']>>[0]>;
export type MuiCloseReasons = Partial<Parameters<NonNullable<MenuProps['onClose']>>[1]>;

export interface FeedsMiniMenuConfig {
  view?: { enabled?: boolean };
  remoteView?: { enabled?: boolean };
  import?: { enabled?: boolean };
}

export const allFeedsMiniMenuOptions: FeedsMiniMenuConfig = {
  view: { enabled: true },
  remoteView: { enabled: true },
  import: { enabled: true },
};

export interface FeedsMiniMenuProps extends Omit<
  BaseMiniMenuProps,
  'selectedItems' | 'contextMenuItem'
> {
  selectedItems?: WireSearchResultItem[];
  menuConfig?: FeedsMiniMenuConfig;
  contextMenuItem?: WireSearchResultItem | null;
  isContextItemDisabled?: boolean;
  anchorEl?: HTMLElement | null;
}

export const FeedsMiniMenu = memo(
  forwardRef<HTMLDivElement, FeedsMiniMenuProps>(
    (
      {
        menuEvent,
        handleMiniMenuClose,
        menuConfig = allFeedsMiniMenuOptions,
        contextMenuItem,
        isContextItemDisabled,
        anchorEl,
      },
      ref,
    ) => {
      const theme = useTheme();
      const { t } = useTranslation();
      const { sessionConfig, activeSite } = useCosmos();
      const { maybeWireImport } = useNxtImport();
      const { selectedChannel, channels } = useWireSearch();
      const location = useLocation();

      // Permission Checks
      const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
      const { granted: userHasFullWire } =
        checkSyndicationFullAccessPermission(sessionConfig);
      const { granted: userHasImportWire, name: importWirePermName } =
        checkSyndicationImportContentPermission(sessionConfig);

      // Determine menu mode: button (anchorEl) or context (menuEvent position)
      const isButtonMode = Boolean(anchorEl);

      // Minimenu UI States
      const menuX = menuEvent?.clientX ?? undefined;
      const menuY = menuEvent?.clientY ?? undefined;
      const hasXY = menuX && menuY;
      const isOpen = isButtonMode ? Boolean(anchorEl) : Boolean(hasXY);
      const anchorPos: MenuProps['anchorPosition'] = hasXY
        ? { top: menuY, left: menuX }
        : undefined;

      const feedItem = contextMenuItem;

      // --------------------------------------------------
      // Event Handlers, Styles
      // --------------------------------------------------
      const iconSx: SxProps<Theme> = useMemo(() => {
        return {
          fontSize: bloxValues.iconSizeRegular,
          color: theme.palette.text.secondary,
          mr: 1,
        };
      }, [theme]);

      const menuSx: SxProps<Theme> = useMemo(() => {
        return {
          '& .MuiMenuItem-root': {
            fontSize: bloxValues.regularFontSizeRem,
          },
        };
      }, []);

      const menuListRef = useRef<HTMLUListElement>(null);

      const internalOnClose = useCallback(
        (event?: MuiCloseEvent, reason?: MuiCloseReasons) => {
          if (event && 'stopPropagation' in event) {
            (event as Event).stopPropagation();
            (event as Event).preventDefault();
          }
          handleMiniMenuClose();
        },
        [handleMiniMenuClose],
      );

      const handleMenuClickCaptures = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
          if (!menuListRef?.current?.contains(e?.target as Node)) {
            e.stopPropagation();
            e.preventDefault();
            internalOnClose(e, 'backdropClick');
            return;
          }
        },
        [internalOnClose],
      );

      // --------------------------------------------------
      // View (Imported Asset) Button
      // --------------------------------------------------
      const viewState: PermissionInfo & { show: boolean } = useMemo(() => {
        const showView = Boolean(menuConfig?.view?.enabled);
        if (userIsAdmin || userHasFullWire || userHasImportWire) {
          const hasAssetUuid = Boolean(feedItem?.asset_uuid);
          return {
            info: hasAssetUuid
              ? t('blox.general.live_view')
              : t('blox.permissions.msg.required_short', {
                  name: importWirePermName,
                }),
            disabled: isContextItemDisabled || !hasAssetUuid,
            show: showView,
          };
        }
        return {
          info: t('blox.permissions.msg.required_short', {
            name: importWirePermName,
          }),
          disabled: true,
          show: showView,
        };
      }, [
        menuConfig?.view?.enabled,
        userIsAdmin,
        userHasFullWire,
        userHasImportWire,
        isContextItemDisabled,
        feedItem?.asset_uuid,
        importWirePermName,
        t,
      ]);

      const handleView = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (viewState?.disabled || !feedItem?.asset_uuid) return;
          const viewUrl = `/${activeSite}/editorial/edit/${feedItem.asset_uuid}/`;
          window.open(viewUrl, '_blank');
          internalOnClose();
        },
        [viewState?.disabled, feedItem?.asset_uuid, activeSite, internalOnClose],
      );

      // --------------------------------------------------
      // Remote View (Feed View) Button
      // --------------------------------------------------
      const remoteViewState: PermissionInfo & { show: boolean } = useMemo(() => {
        const showRemoteView = Boolean(menuConfig?.remoteView?.enabled);
        if (userIsAdmin || userHasFullWire || userHasImportWire) {
          const channel =
            selectedChannel || new URLSearchParams(location?.search)?.get('channel_id');
          const itemId = feedItem?.object_id;
          const isDeleted = feedItem?.status === 'deleted';
          const canView = !isDeleted && isAssetTypeEditorEnabled(feedItem?.type);
          const isRoutable = Boolean(channel && itemId && canView);
          return {
            info: t('blox.editorial.syndication.actions.view_original'),
            disabled: isContextItemDisabled || !isRoutable,
            show: showRemoteView,
          };
        }
        return {
          info: t('blox.permissions.msg.required_short', {
            name: importWirePermName,
          }),
          disabled: true,
          show: showRemoteView,
        };
      }, [
        menuConfig?.remoteView?.enabled,
        userIsAdmin,
        userHasFullWire,
        userHasImportWire,
        isContextItemDisabled,
        selectedChannel,
        location?.search,
        feedItem?.object_id,
        feedItem?.status,
        feedItem?.type,
        importWirePermName,
        t,
      ]);

      const handleRemoteView = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (remoteViewState?.disabled) return;
          const channel =
            selectedChannel || new URLSearchParams(location?.search)?.get('channel_id');
          const itemId = feedItem?.object_id;
          if (!channel || !itemId) {
            console.error('[FeedsMiniMenu] Missing channel or item id for remote view');
            internalOnClose();
            return;
          }
          try {
            const encodedId = encodeURIComponent(itemId);
            const remoteViewUrl = `/${activeSite}/feeds/view/${channel}/${encodedId}/`;
            window.open(remoteViewUrl, '_blank');
          } catch (error) {
            console.error('[FeedsMiniMenu] Error encoding item id:', error);
          }
          internalOnClose();
        },
        [
          remoteViewState?.disabled,
          selectedChannel,
          location?.search,
          feedItem?.object_id,
          activeSite,
          internalOnClose,
        ],
      );

      // --------------------------------------------------
      // Import (Add to Site) Button
      // --------------------------------------------------
      const importState: PermissionInfo & { show: boolean } = useMemo(() => {
        const showImport = Boolean(menuConfig?.import?.enabled);
        if (userIsAdmin || userHasFullWire || userHasImportWire) {
          const isDisabled =
            isContextItemDisabled ||
            feedItem?.status === 'imported' ||
            feedItem?.status === 'importing';
          return {
            info: t('blox.editorial.syndication.actions.add_to_site'),
            disabled: isDisabled,
            show: showImport,
          };
        }
        return {
          info: t('blox.permissions.msg.required_short', {
            name: importWirePermName,
          }),
          disabled: true,
          show: showImport,
        };
      }, [
        menuConfig?.import?.enabled,
        userIsAdmin,
        userHasFullWire,
        userHasImportWire,
        isContextItemDisabled,
        feedItem?.status,
        importWirePermName,
        t,
      ]);

      const handleImport = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (importState?.disabled) return;
          if (!selectedChannel) {
            console.error('[FeedsMiniMenu] No channel selected for wire import');
            internalOnClose();
            return;
          }
          const channelName =
            channels.find((ch) => ch.value === selectedChannel)?.label || null;
          maybeWireImport(feedItem, selectedChannel, channelName);
          internalOnClose();
        },
        [
          importState?.disabled,
          selectedChannel,
          channels,
          maybeWireImport,
          feedItem,
          internalOnClose,
        ],
      );

      return (
        <Fragment>
          <Menu
            ref={ref}
            data-testid="feeds-minimenu"
            disableEnforceFocus={true}
            open={isOpen}
            onClose={internalOnClose}
            sx={menuSx}
            hideBackdrop={true}
            onMouseDownCapture={handleMenuClickCaptures}
            onContextMenuCapture={handleMenuClickCaptures}
            {...(isButtonMode
              ? { anchorEl }
              : {
                  anchorReference: 'anchorPosition' as const,
                  anchorPosition: anchorPos,
                  TransitionProps: { timeout: { enter: 0, exit: 0 } },
                })}
            slotProps={{
              list: {
                autoFocusItem: true,
                ref: menuListRef,
              },
            }}
          >
            {viewState?.show ? (
              <MenuItem
                data-testid="feeds-minimenu-view"
                disabled={viewState?.disabled}
                onClick={handleView}
              >
                <PublicIcon sx={iconSx} />
                {t('blox.actions.view')}
              </MenuItem>
            ) : null}
            {remoteViewState?.show ? (
              <MenuItem
                data-testid="feeds-minimenu-remote-view"
                disabled={remoteViewState?.disabled}
                onClick={handleRemoteView}
              >
                <PreviewIcon sx={iconSx} />
                {t('blox.editorial.syndication.actions.view_remote')}
              </MenuItem>
            ) : null}
            {importState?.show ? (
              <MenuItem
                data-testid="feeds-minimenu-import"
                disabled={importState?.disabled}
                onClick={handleImport}
              >
                <AddCircleOutlineIcon sx={iconSx} />
                {t('blox.editorial.syndication.actions.add_to_site')}
              </MenuItem>
            ) : null}
          </Menu>
        </Fragment>
      );
    },
  ),
);
FeedsMiniMenu.displayName = 'FeedsMiniMenu';
